import { useId } from 'react';
import { PiXLight } from 'react-icons/pi';
import Button from '../atoms/buttons/button';
import { Link } from '../atoms/links/link';
import { Text } from '../atoms/text';
import { AppNoticeImage } from '../atoms/app-notice-image';
import { useAppNotices } from '../../hooks/provider-bounded/use-app-notices';
import { useI18n } from '../../hooks/provider-bounded/i18n';
import { useMediaQuery } from '../../hooks/use-media-query';

/**
 * Displays the selected notice without interpreting its business purpose.
 * @returns A persistent polite live region and at most one responsive notice.
 */
export const AppNoticeRegion = () => {
  const { activeNotice: notice } = useAppNotices();
  const { t } = useI18n();
  const descriptionId = useId();
  // Match the resolved Tailwind md breakpoint used by the authenticated sidebar.
  const isMobile = !useMediaQuery('(min-width: 720px)');
  const compact = isMobile && notice?.compactOnMobile;
  const actions = notice ? [notice.primaryAction, notice.secondaryAction] : [];
  return (
    <div aria-live="polite" aria-atomic="true" className="shrink-0">
      {notice && (
        <section
          aria-label={notice.title}
          className={`flex flex-wrap items-center bg-blue-50 text-secondary-900 ${
            compact
              ? 'min-h-[44px] gap-2 px-2'
              : 'gap-3 border-b border-blue-100 px-4 py-3 md:gap-4 md:px-5'
          }`}
        >
          {notice.icon && !(compact && notice.hideIconOnMobile) && (
            <div
              aria-hidden="true"
              className={
                compact
                  ? 'flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4'
                  : 'hidden h-9 w-9 shrink-0 items-center justify-center rounded-full border border-blue-100 bg-white md:flex'
              }
            >
              {notice.icon}
            </div>
          )}
          {!compact && notice.image && <AppNoticeImage {...notice.image} />}
          <div className={`min-w-0 flex-1 basis-36 ${compact ? 'py-1' : ''}`}>
            <Text
              as="p"
              size="small"
              weight="medium"
              className={
                compact ? 'break-words !text-[13px] !leading-[18px]' : ''
              }
              disableAutoLink
            >
              {isMobile ? notice.mobileTitle ?? notice.title : notice.title}
            </Text>
            {!compact && (
              <Text
                as="p"
                size="tiny"
                color="secondary"
                className="mt-1"
                disableAutoLink
              >
                {isMobile
                  ? notice.mobileMessage ?? notice.message
                  : notice.message}
              </Text>
            )}
          </div>
          <div
            className={`flex max-w-full flex-wrap items-center ${compact ? 'gap-1' : 'gap-2'}`}
          >
            {actions.map(
              (action, index) =>
                action && (
                  <div key={index} className="flex max-w-full flex-col gap-1">
                    <Link
                      mode="action"
                      size="small"
                      variant="neutral"
                      className={
                        compact
                          ? 'min-h-[44px] min-w-[44px] max-w-full whitespace-normal break-words text-[13px] leading-[18px]'
                          : 'max-w-full whitespace-normal break-words'
                      }
                      onClick={action.onAction}
                      loading={action.pending}
                      disabled={action.disabled}
                      aria-busy={action.pending ?? undefined}
                      aria-describedby={
                        action.explanation
                          ? `${descriptionId}-${index}`
                          : undefined
                      }
                    >
                      {compact
                        ? action.mobileLabel ?? action.label
                        : action.label}
                    </Link>
                    {action.explanation && (
                      <div id={`${descriptionId}-${index}`}>
                        <Text size="tiny" color="secondary" disableAutoLink>
                          {action.explanation}
                        </Text>
                      </div>
                    )}
                  </div>
                )
            )}
            {notice.onDismiss && (
              <Button
                type="button"
                variant="neutral"
                appearance="ghost"
                isIconOnly
                className={
                  compact ? 'min-h-[44px] min-w-[44px] !h-auto' : undefined
                }
                aria-label={t('shared.notices.dismiss')}
                onClick={notice.onDismiss}
              >
                <PiXLight
                  className={compact ? 'h-4 w-4' : 'h-5 w-5'}
                  aria-hidden="true"
                />
              </Button>
            )}
          </div>
        </section>
      )}
    </div>
  );
};
